import { useCallback, useRef, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Center,
  FileButton,
  Group,
  Loader,
  Paper,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconMusic, IconPlayerPlay, IconPlayerStop, IconTrash, IconUpload } from '@tabler/icons-react';
import { createFileRoute } from '@tanstack/react-router';
import { useSubscription } from 'urql';
import { ErrorAlert } from '@/components/error-alert';
import { api } from '@/lib/api';
import { getErrorMessage } from '@/lib/error-message';
import { useOrg } from '@/hooks/use-org';
import { uploadAudioFile } from '@/widgets/audio-files';
import { buildAudioFileUsages, usageLabel } from './-components/audio-file-usages';
import type { AudioFileUsage } from './-components/audio-file-usages';
import { AudioFileFlowUsageSubscription, AudioFilesSubscription } from './-components/graphql';

export const Route = createFileRoute('/_authed/settings/media')({
  component: SettingsMediaPage,
});

function PreviewButton({
  isPlaying,
  isLoading,
  onToggle,
}: {
  isPlaying: boolean;
  isLoading: boolean;
  onToggle: () => void;
}) {
  return (
    <Tooltip label={isPlaying ? 'Stop preview' : 'Play preview'}>
      <ActionIcon variant="light" color="indigo" loading={isLoading} onClick={onToggle}>
        {isPlaying ? <IconPlayerStop size={14} /> : <IconPlayerPlay size={14} />}
      </ActionIcon>
    </Tooltip>
  );
}

function Media({ organizationId }: { organizationId: string }) {
  const [{ data }] = useSubscription({ query: AudioFilesSubscription, variables: { organizationId } });
  const [{ data: flowData }] = useSubscription({
    query: AudioFileFlowUsageSubscription,
    variables: { organizationId },
  });

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewLoadingId, setPreviewLoadingId] = useState<string | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);

  const attachAudio = useCallback((element: HTMLAudioElement | null) => {
    audioRef.current = element;

    return () => {
      element?.pause();
      audioRef.current = null;
      setPlayingId(null);
    };
  }, []);

  if (!data || !flowData) {
    return (
      <Center h="30vh">
        <Loader size="lg" />
      </Center>
    );
  }

  const flowUsages = buildAudioFileUsages(flowData.flows);

  const upload = async (file: File | null) => {
    if (!file) return;

    setUploadError(null);
    setIsUploading(true);

    try {
      await uploadAudioFile(organizationId, file);
    } catch (error) {
      setUploadError(getErrorMessage(error, 'Upload failed'));
    } finally {
      setIsUploading(false);
    }
  };

  const remove = async (id: string) => {
    setRemoveError(null);
    setRemovingId(id);

    const { error } = await api['audio-files']({ id }).delete();

    setRemovingId(null);
    setConfirmDeleteId(null);

    if (error) setRemoveError(getErrorMessage(error, 'Failed to delete audio media'));
  };

  const stopPreview = () => {
    audioRef.current?.pause();
    setPlayingId(null);
  };

  const playPreview = async (id: string) => {
    const element = audioRef.current;

    if (!element) return;

    setPreviewError(null);
    setPreviewLoadingId(id);

    const { data: link, error } = await api['audio-files']({ id })['access-link'].get();

    setPreviewLoadingId(null);

    if (error) return setPreviewError(getErrorMessage(error, 'Preview not available'));

    element.src = link.url;
    setPlayingId(id);

    await element.play().catch((cause: unknown) => {
      setPlayingId(null);
      setPreviewError(getErrorMessage(cause, 'Preview could not be played'));
    });
  };

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-end">
        <div>
          <Title order={2}>Media Library</Title>
          <Text size="sm" c="dimmed" mt="2xs">
            Upload and manage audio media for widgets. Audio in active use cannot be removed.
          </Text>
        </div>
        <FileButton onChange={(file) => void upload(file)} accept="audio/*,.aac,.flac,.m4a,.mp3,.ogg,.wav,.webm">
          {(fileButtonProps) => (
            <Button {...fileButtonProps} leftSection={<IconUpload size={16} />} loading={isUploading}>
              Upload Audio
            </Button>
          )}
        </FileButton>
      </Group>

      <ErrorAlert message={uploadError} />
      <ErrorAlert message={removeError} />
      <ErrorAlert message={previewError} />

      {data.audioFiles.length === 0 ? (
        <Paper p="3xl" radius="lg" withBorder>
          <Stack align="center" gap="md">
            <ThemeIcon size={48} radius="xl" variant="light" color="gray">
              <IconMusic size={24} />
            </ThemeIcon>
            <div style={{ textAlign: 'center' }}>
              <Text fw={500}>No audio uploaded yet</Text>
              <Text size="sm" c="dimmed" mt="2xs">
                Upload audio here and it will become available in the Play widget media catalog.
              </Text>
            </div>
          </Stack>
        </Paper>
      ) : (
        <Paper p={0} radius="lg" style={{ overflow: 'hidden' }} withBorder>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th w={90}>Preview</Table.Th>
                <Table.Th>Type</Table.Th>
                <Table.Th>Uploaded</Table.Th>
                <Table.Th>Usage</Table.Th>
                <Table.Th w={90} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data.audioFiles.map((audioFile) => {
                const usages: AudioFileUsage[] = [
                  ...(flowUsages.get(audioFile.id) ?? []),
                  ...audioFile.queues.map((queue) => ({
                    kind: 'queue_hold_music' as const,
                    entityId: queue.id,
                    name: queue.name,
                  })),
                ];

                return (
                  <Table.Tr key={audioFile.id}>
                    <Table.Td>
                      <Stack gap={2}>
                        <Text size="sm" fw={500}>
                          {audioFile.name}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {Math.round(audioFile.durationSeconds ?? 0) > 0
                            ? `${Math.round(audioFile.durationSeconds ?? 0)} seconds`
                            : 'Duration unavailable'}
                        </Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <PreviewButton
                        isPlaying={playingId === audioFile.id}
                        isLoading={previewLoadingId === audioFile.id}
                        onToggle={() => (playingId === audioFile.id ? stopPreview() : void playPreview(audioFile.id))}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" color="blue">
                        {audioFile.mimeType}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {new Date(audioFile.uploadedAt).toLocaleString()}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {usages.length === 0 ? (
                        <Badge variant="light" color="green">
                          Unused
                        </Badge>
                      ) : (
                        <Stack gap={2}>
                          {usages.map((usage) => (
                            <Text key={`${usage.kind}-${usage.entityId}`} size="xs" c="dimmed">
                              {usageLabel(usage)}
                            </Text>
                          ))}
                        </Stack>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {confirmDeleteId === audioFile.id ? (
                        <Group gap={4}>
                          <Button size="compact-xs" variant="subtle" onClick={() => setConfirmDeleteId(null)}>
                            No
                          </Button>
                          <Button
                            size="compact-xs"
                            color="red"
                            loading={removingId === audioFile.id}
                            onClick={() => void remove(audioFile.id)}
                          >
                            Yes
                          </Button>
                        </Group>
                      ) : (
                        <ActionIcon variant="subtle" color="red" onClick={() => setConfirmDeleteId(audioFile.id)}>
                          <IconTrash size={14} />
                        </ActionIcon>
                      )}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      <audio ref={attachAudio} preload="none" onEnded={() => setPlayingId(null)} style={{ display: 'none' }} />
    </Stack>
  );
}

function SettingsMediaPage() {
  const { organizationId } = useOrg();

  if (!organizationId) {
    return (
      <Center h="30vh">
        <Loader size="lg" />
      </Center>
    );
  }

  return <Media organizationId={organizationId} />;
}
